// Bank review API — /onboarding/review* (needs an EMPLOYEE or ADMIN token).
// This is the bank's side of the flow: pick an application up, sign off or
// reject the video KYC audit, then approve (opens the account and issues the
// temporary-password delivery) or reject. Used by the dev-only bank desk.
import { get, getBlob, isRealApiEnabled, post, put } from './client';
import { issueTemporaryPassword } from './auth';
import { getVideoKyc } from './vkyc';
import { getMockOnboardingApplication, updateMockOnboardingApplication } from './onboarding';
import { markMockVerified } from './activation';
import type { ApplicationData } from './onboarding';

export interface ReviewQueueItem {
  applicationId: string;
  applicationReference: string;
  applicantName?: string;
  fullName?: string;
  accountType?: string;
  status?: string;
  state?: string;
  submittedAt?: string;
}

export type ReviewLifecycleState =
  | 'PENDING'
  | 'UNDER_VERIFICATION'
  | 'ACTIVE_LIMITED_KYC'
  | 'ACTIVE_FULL_KYC'
  | 'REJECTED'
  | 'INACTIVE_KYC_EXPIRED';

export type ReviewApplicationStatus =
  | 'STARTED'
  | 'CONTACT_VERIFIED'
  | 'DOCUMENTS_UPLOADED'
  | 'SIGNATURE_CAPTURED'
  | 'PROFILE_COMPLETED'
  | 'SUBMITTED'
  | 'RETURNED'
  | 'APPROVED'
  | 'REJECTED'
  | 'ABANDONED';

export type ReviewAccountType =
  | 'SAVINGS'
  | 'CURRENT'
  | 'SALARY'
  | 'FIXED_DEPOSIT'
  | 'RECURRING_DEPOSIT';

export interface ReviewQueue {
  content?: ReviewQueueItem[];
  items?: ReviewQueueItem[];
  totalPages?: number;
  totalElements?: number;
  first?: boolean;
  last?: boolean;
  number?: number;
  size?: number;
  [key: string]: unknown;
}

export interface ReviewQueueFilters {
  state?: ReviewLifecycleState;
  status?: ReviewApplicationStatus;
  accountType?: ReviewAccountType;
  branchCode?: string;
  assignedTo?: string;
  mine?: boolean;
  submittedFrom?: string;
  submittedTo?: string;
  page?: number;
  size?: number;
  sort?: string[];
}

export type ReviewReturnStep =
  | 'PERSONAL_DETAILS'
  | 'CONTACT_VERIFICATION'
  | 'DOCUMENTS'
  | 'SIGNATURE'
  | 'PROFILE'
  | 'DIGITAL_BANKING'
  | 'VIDEO_KYC';

export type ReviewDocumentKind =
  | 'AADHAAR_FRONT'
  | 'AADHAAR_BACK'
  | 'PAN_CARD'
  | 'SELFIE'
  | 'SIGNATURE';

export interface ApproveResult {
  applicationReference: string;
  customerId: string;
  accountNumber?: string;
  ifsc?: string;
  branchName?: string;
  accountType?: string;
  lifecycleState?: string;
  kycLevel?: string;
  activatedAt?: string;
  temporaryPasswordIssued?: boolean;
  temporaryPasswordExpiresAt?: string;
}

export interface AdminUser {
  [key: string]: unknown;
}

export interface UpdateAdminUserInput {
  [key: string]: unknown;
}

export interface UpdateAdminUserStatusInput {
  [key: string]: unknown;
}

export interface AdminUserPage {
  content: AdminUser[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
  first?: boolean;
  last?: boolean;
  [key: string]: unknown;
}

export interface AdminUserAuditEntry {
  id: string;
  actorUsername: string;
  action: string;
  targetType: string;
  targetId: string;
  beforeValue?: string;
  afterValue?: string;
  reason?: string;
  performedAt: string;
}

export interface AdminUserAuditPage {
  content: AdminUserAuditEntry[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
  first?: boolean;
  last?: boolean;
  [key: string]: unknown;
}

export type AdminSettingValueType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON';

export interface AdminSetting {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  settingKey: string;
  value: string;
  valueType: AdminSettingValueType | string;
  category: string;
  label: string;
  description: string;
  systemManaged: boolean;
  minValue: number | null;
  maxValue: number | null;
  allowedValues: string;
}

export type UpdateAdminSettingInput = Record<string, unknown>;

const adminUserPath = (userId: number | string): string =>
  `/admin/users/${encodeURIComponent(String(userId))}`;

export const getAdminUser = (userId: number | string): Promise<AdminUser> =>
  get<AdminUser>(adminUserPath(userId));

export const updateAdminUser = (
  userId: number | string,
  input: UpdateAdminUserInput,
): Promise<AdminUser> =>
  put<AdminUser>(adminUserPath(userId), input);

export const unlockAdminUser = (userId: number | string): Promise<AdminUser> =>
  post<AdminUser>(`${adminUserPath(userId)}/unlock`, {});

export const updateAdminUserStatus = (
  userId: number | string,
  input: UpdateAdminUserStatusInput,
): Promise<AdminUser> =>
  post<AdminUser>(`${adminUserPath(userId)}/status`, input);

export const grantAdminUserRole = (
  userId: number | string,
  input: Record<string, unknown>,
): Promise<AdminUser> =>
  post<AdminUser>(`${adminUserPath(userId)}/roles`, input);

export const revokeAdminUserRole = (
  userId: number | string,
  input: Record<string, unknown>,
): Promise<AdminUser> =>
  post<AdminUser>(`${adminUserPath(userId)}/roles/revoke`, input);

export const resetAdminUserPassword = (userId: number | string): Promise<AdminUser> =>
  post<AdminUser>(`${adminUserPath(userId)}/password/reset`);

export const listAdminUsers = (input: {
  q?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  page?: number;
  size?: number;
} = {}): Promise<AdminUserPage> =>
  get<AdminUserPage>('/admin/users', {
    params: {
      ...(input.q ? { q: input.q } : {}),
      ...(input.status ? { status: input.status } : {}),
      page: input.page ?? 0,
      size: input.size ?? 20,
    },
  });

export const getAdminUserAudit = (
  userId: number | string,
  page = 0,
  size = 20,
): Promise<AdminUserAuditPage> =>
  get<AdminUserAuditPage>(`${adminUserPath(userId)}/audit`, {
    params: { page, size },
  });

const adminSettingsPath = '/admin/settings';

export const getAdminSetting = (key: string): Promise<AdminSetting> =>
  get<AdminSetting>(`${adminSettingsPath}/${encodeURIComponent(key)}`);

export const updateAdminSetting = (
  key: string,
  input: UpdateAdminSettingInput,
): Promise<AdminSetting> =>
  put<AdminSetting>(`${adminSettingsPath}/${encodeURIComponent(key)}`, input);

export const listAdminSettings = (category?: string): Promise<AdminSetting[]> =>
  get<AdminSetting[]>(adminSettingsPath, {
    params: category ? { category } : undefined,
  });

export type DebitCardDispatchStatus =
  | 'NOT_ORDERED'
  | 'ORDERED'
  | 'DISPATCHED'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'ACTIVE'
  | 'LOCKED'
  | 'BLOCKED'
  | 'REISSUE_PENDING'
  | 'EXPIRED';

export type CreditCardApplicationStatus =
  | 'SUBMITTED'
  | 'VERIFICATION'
  | 'APPROVED'
  | 'DISPATCHED'
  | 'DELIVERED'
  | 'REJECTED';

export type CaseResolutionStatus = 'IN_PROGRESS' | 'RESOLVED' | 'REJECTED' | 'CLOSED';

export const decideLoanApplication = (
  applicationId: string,
  approved: boolean,
  reason?: string,
): Promise<Record<string, unknown>> =>
  post<Record<string, unknown>>(
    `/admin/operations/loan-applications/${encodeURIComponent(applicationId)}/decision`,
    undefined,
    { params: { approved, ...(reason ? { reason } : {}) } },
  );

export const updateDebitCardDispatch = (
  cardId: string,
  status: DebitCardDispatchStatus,
): Promise<Record<string, unknown>> =>
  post<Record<string, unknown>>(
    `/admin/operations/debit-cards/${encodeURIComponent(cardId)}/dispatch`,
    undefined,
    { params: { status } },
  );

export const updateCreditCardApplicationDispatch = (
  applicationId: string,
  status: CreditCardApplicationStatus,
): Promise<Record<string, unknown>> =>
  post<Record<string, unknown>>(
    `/admin/operations/credit-card-applications/${encodeURIComponent(applicationId)}/dispatch`,
    undefined,
    { params: { status } },
  );

export const decideCreditCardApplication = (
  applicationId: string,
  approved: boolean,
  reason?: string,
): Promise<Record<string, unknown>> =>
  post<Record<string, unknown>>(
    `/admin/operations/credit-card-applications/${encodeURIComponent(applicationId)}/decision`,
    undefined,
    { params: { approved, ...(reason ? { reason } : {}) } },
  );

export const resolveAdminCase = (
  caseId: string,
  status: CaseResolutionStatus = 'RESOLVED',
  resolution?: string,
): Promise<Record<string, unknown>> =>
  post<Record<string, unknown>>(
    `/admin/operations/cases/${encodeURIComponent(caseId)}/resolve`,
    undefined,
    { params: { status, ...(resolution ? { resolution } : {}) } },
  );

const base = (applicationId: string) =>
  `/onboarding/review/${encodeURIComponent(applicationId)}`;

/** Review queue with composable filters and pageable sorting. */
export const getReviewQueue = (filters: ReviewQueueFilters = {}): Promise<ReviewQueue> => {
  if (!isRealApiEnabled()) {
    const application = getMockOnboardingApplication();
    const items = application && application.status !== 'ACTIVE' ? [{ applicationId: application.applicationId, applicationReference: application.applicationReference, applicantName: application.fullName, fullName: application.fullName, accountType: application.accountType, status: application.status, state: application.lifecycleState ?? application.status, submittedAt: application.submittedAt }] : [];
    return Promise.resolve({ content: items, items, totalPages: items.length ? 1 : 0, totalElements: items.length, first: true, last: true, number: 0, size: filters.size ?? 20 });
  }
  return get<ReviewQueue>('/onboarding/review', {
    params: {
      ...(filters.state ? { state: filters.state } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.accountType ? { accountType: filters.accountType } : {}),
      ...(filters.branchCode ? { branchCode: filters.branchCode } : {}),
      ...(filters.assignedTo ? { assignedTo: filters.assignedTo } : {}),
      ...(filters.mine !== undefined ? { mine: filters.mine } : {}),
      ...(filters.submittedFrom ? { submittedFrom: filters.submittedFrom } : {}),
      ...(filters.submittedTo ? { submittedTo: filters.submittedTo } : {}),
      page: filters.page ?? 0,
      size: filters.size ?? 20,
      ...(filters.sort?.length ? { sort: filters.sort } : {}),
    },
  });
};

export const getReviewApplication = (applicationId: string): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) {
    const application = getMockOnboardingApplication();
    if (!application || application.applicationId !== applicationId) throw new Error('Mock application not found.');
    return Promise.resolve(application);
  }
  return get<ApplicationData>(base(applicationId));
};

export const getReviewDocument = (
  applicationId: string,
  kind: ReviewDocumentKind,
  download = false,
): Promise<Blob> => {
  if (!isRealApiEnabled()) return Promise.resolve(new Blob([`Mock document: ${kind}`], { type: 'text/plain' }));
  return getBlob(`${base(applicationId)}/documents/${encodeURIComponent(kind)}`, { params: { download } });
};

/** Picks the application up — marks it UNDER_VERIFICATION. */
export const startReview = (applicationId: string): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'UNDER_VERIFICATION', lifecycleState: 'UNDER_VERIFICATION' }));
  return post<unknown>(`${base(applicationId)}/start`, {});
};

/** Returns an assigned application to the open review queue. */
export const unassignApplication = (applicationId: string): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'SUBMITTED', lifecycleState: 'PENDING' }));
  return post<unknown>(`${base(applicationId)}/unassign`, {});
};

/** Returns an application for correction at the requested onboarding step. */
export const returnApplication = (
  applicationId: string,
  step: ReviewReturnStep = 'DOCUMENTS',
  reason?: string,
): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'RETURNED', currentStep: step === 'DOCUMENTS' ? 5 : 7 }));
  return post<unknown>(`${base(applicationId)}/return`, { step, ...(reason ? { reason } : {}) });
};

/** Reopens a rejected or returned application for review. */
export const reopenApplication = (applicationId: string): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'SUBMITTED', lifecycleState: 'PENDING' }));
  return post<unknown>(`${base(applicationId)}/reopen`, {});
};

export const approveVideoKyc = (applicationId: string): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'KYC_APPROVED', lifecycleState: 'ACTIVE_FULL_KYC' }));
  return post<unknown>(`${base(applicationId)}/video-kyc/approve`, {});
};

export const rejectVideoKyc = (applicationId: string, reason: string): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'KYC_REJECTED', lifecycleState: 'REJECTED' }));
  return post<unknown>(`${base(applicationId)}/video-kyc/reject`, { reason });
};

/** Approves and opens the account; the customer then receives a temporary password. */
export const approveApplication = (applicationId: string): Promise<ApproveResult> => {
  if (!isRealApiEnabled()) {
    const application = getMockOnboardingApplication();
    if (!application || application.applicationId !== applicationId) throw new Error('Mock application not found.');
    const activatedAt = new Date().toISOString();
    const accountNumber = `1000000${String(Math.abs(application.applicationId.split('').reduce((hash, ch) => ((hash * 31 + ch.charCodeAt(0)) >>> 0), 0)) % 100000).padStart(5, '0')}`;
    const updated = updateMockOnboardingApplication({ status: 'ACTIVE', lifecycleState: 'ACTIVE_FULL_KYC', accountNumber, ifsc: 'HDFC0000001', branchName: 'MG Road Branch', activatedAt, kycLevel: 'FULL' });
    issueTemporaryPassword(updated.email ?? updated.maskedEmail);
    markMockVerified({ reference: updated.applicationReference, customerId: updated.customerId, fullName: updated.fullName, accountType: updated.accountType, maskedMobile: updated.maskedMobile, maskedEmail: updated.maskedEmail });
    return Promise.resolve({ applicationReference: updated.applicationReference, customerId: updated.customerId, accountNumber, ifsc: 'HDFC0000001', branchName: 'MG Road Branch', accountType: updated.accountType, lifecycleState: 'ACTIVE_FULL_KYC', kycLevel: 'FULL', activatedAt, temporaryPasswordIssued: true, temporaryPasswordExpiresAt: new Date(Date.now() + 72 * 3600000).toISOString() });
  }
  return post<ApproveResult>(`${base(applicationId)}/approve`, {});
};

export const rejectApplication = (
  applicationId: string,
  reason: string,
): Promise<ApplicationData> => {
  if (!isRealApiEnabled()) return Promise.resolve(updateMockOnboardingApplication({ status: 'REJECTED', lifecycleState: 'REJECTED' }));
  return post<ApplicationData>(`${base(applicationId)}/reject`, { reason });
};

export const reassignApplication = (
  applicationId: string,
  officer: string,
): Promise<unknown> =>
  post<unknown>(`${base(applicationId)}/reassign`, undefined, {
    params: { officer },
  });

/** Dev/test only: runs the whole bank-verification chain in one go so an
 *  application waiting at "Bank verification" reaches ACTIVE without a manual
 * employee pass. start → sign off the video KYC (if the audit is pending) →
 * approve. Returns the approve result (account + temporary-password status). */
export const verifyApplicationEmployer = (
  applicationId: string,
  verified = true,
): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve({ applicationId, verified });
  return post<unknown>(`${base(applicationId)}/employer/verify`, undefined, { params: { verified } });
};

export const verifyApplicationBusinessProfile = (
  applicationId: string,
  verified = true,
): Promise<unknown> => {
  if (!isRealApiEnabled()) return Promise.resolve({ applicationId, verified });
  return post<unknown>(`${base(applicationId)}/business-profile/verify`, undefined, { params: { verified } });
};

export const autoVerifyApplication = async (applicationId: string): Promise<ApproveResult> => {
  if (!isRealApiEnabled()) {
    await startReview(applicationId);
    await approveVideoKyc(applicationId);
    return approveApplication(applicationId);
  }
  await startReview(applicationId).catch(() => undefined);
  const vk = await getVideoKyc(applicationId).catch(() => null);
  if (vk && (vk.state === 'DONE_AUDIT_PENDING' || vk.state === 'IN_PROGRESS')) {
    await approveVideoKyc(applicationId).catch(() => undefined);
  }
  return approveApplication(applicationId);
};
